import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { TOOLS } from '../../tools';

import type { ITool } from '../../tools';

/**
 * Global keyboard-first tool switcher. Mounted once at the top of `MainApp`,
 * listens at the window level for Cmd+K (or Ctrl+K on non-mac), shows a
 * centered overlay with a filtered list of tools, and navigates on Enter.
 *
 * Fuzzy match: a tool matches the query when every whitespace-separated token
 * appears (case-insensitive, substring) in the tool's name + keywords. This is
 * good enough for a registry of ~20 tools and has no dependency.
 *
 * Keyboard:
 *   - Cmd+K / Ctrl+K   — toggle open (also closes if already open)
 *   - ArrowDown / ↓    — move selection down
 *   - ArrowUp   / ↑    — move selection up
 *   - Enter             — navigate to the selected tool
 *   - Esc               — close without navigating
 */
const CommandPalette = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const matches = React.useMemo(() => filterTools(TOOLS, query), [query]);

  // Global toggle: Cmd+K / Ctrl+K anywhere in the app.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Reset query + selection whenever the palette opens; close whenever the
  // route changes (user navigated via Enter, keyboard shortcut, or otherwise).
  React.useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      // Wait a tick for the input to mount.
      void Promise.resolve().then(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  React.useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Clamp selection when the filtered list shrinks.
  React.useEffect(() => {
    if (selectedIndex >= matches.length) {
      setSelectedIndex(Math.max(0, matches.length - 1));
    }
  }, [matches.length, selectedIndex]);

  const handleSelect = React.useCallback(
    (tool: ITool) => {
      setIsOpen(false);
      void navigate(tool.path);
    },
    [navigate]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => (matches.length === 0 ? 0 : (i + 1) % matches.length));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => (matches.length === 0 ? 0 : (i - 1 + matches.length) % matches.length));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const tool = matches[selectedIndex];
      if (tool) {
        handleSelect(tool);
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  const activeTool = matches[selectedIndex];
  const activeDescendantId = activeTool ? `cmd-option-${activeTool.id}` : undefined;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="bg-bg-overlay/40 fixed inset-0 z-50 flex items-start justify-center pt-24 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setIsOpen(false);
        }
      }}
    >
      <div className="border-border-primary bg-bg-primary flex w-full max-w-xl flex-col border shadow-lg">
        <div className="border-border-primary border-b">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Jump to tool…"
            aria-label="Search tools"
            aria-controls="cmd-options"
            aria-activedescendant={activeDescendantId}
            className="text-fg-primary placeholder:text-fg-quaternary w-full border-0 bg-transparent px-4 py-3 font-mono text-sm outline-none"
          />
        </div>
        <ul id="cmd-options" className="max-h-80 overflow-y-auto py-1" role="listbox" aria-label="Matching tools">
          {matches.length === 0 ? (
            <li className="text-fg-tertiary px-4 py-3 text-xs italic">No tools match.</li>
          ) : (
            matches.map((tool, i) => {
              const Icon = tool.icon;
              const isSelected = i === selectedIndex;
              return (
                <li
                  key={tool.id}
                  id={`cmd-option-${tool.id}`}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setSelectedIndex(i)}
                  onClick={() => handleSelect(tool)}
                  className={cx(
                    'flex cursor-pointer items-center gap-3 px-4 py-2 text-sm',
                    isSelected ? 'bg-bg-secondary' : 'hover:bg-bg-secondary'
                  )}
                >
                  <Icon
                    className={cx(
                      'h-6 w-6 shrink-0 transition-colors',
                      isSelected ? 'text-fg-brand-primary' : 'text-fg-secondary'
                    )}
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="text-fg-primary truncate font-medium">{tool.name}</span>
                    <span className="text-fg-tertiary truncate text-xs">{tool.description}</span>
                  </span>
                  <span className="text-fg-quaternary shrink-0 font-mono text-xs">{tool.path}</span>
                </li>
              );
            })
          )}
        </ul>
        <div className="border-border-primary text-fg-quaternary flex items-center gap-4 border-t px-4 py-2 font-mono text-[10px] uppercase tracking-wide">
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
};

function filterTools(tools: ITool[], query: string): ITool[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) {
    return tools;
  }
  const tokens = q.split(/\s+/);
  return tools.filter((tool) => {
    const haystack = [tool.name, ...tool.keywords].join(' ').toLowerCase();
    return tokens.every((token) => haystack.includes(token));
  });
}

export default CommandPalette;
export { filterTools };
